import React, { createContext, useRef, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { SceneStore } from 'src/types';

const initialState = () => {
  return createStore<SceneStore>((set, get) => {
    type Snapshot = Pick<SceneStore, 'connectors' | 'textBoxes'>;
    const undoStack: Snapshot[] = [];
    const redoStack: Snapshot[] = [];
    let lastMutationAt = 0;
    let isRestoring = false;
    const snapshot = (): Snapshot => structuredClone({ connectors: get().connectors, textBoxes: get().textBoxes });
    const trackedSet: typeof set = (partial, replace) => {
      if (!isRestoring) {
        const now = Date.now();
        if (now - lastMutationAt > 250) {
          undoStack.push(snapshot());
          if (undoStack.length > 100) undoStack.shift();
        }
        lastMutationAt = now;
        redoStack.length = 0;
      }
      set(partial as never, replace as never);
      set({ historyVersion: get().historyVersion + 1 });
    };
    const restore = (next: Snapshot) => {
      isRestoring = true;
      set({ ...next, historyVersion: get().historyVersion + 1 });
      isRestoring = false;
      lastMutationAt = 0;
    };
    return {
      connectors: {},
      textBoxes: {},
      historyVersion: 0,
      actions: {
        get,
        set: trackedSet,
        undo: () => { const next = undoStack.pop(); if (next) { redoStack.push(snapshot()); restore(next); } },
        redo: () => { const next = redoStack.pop(); if (next) { undoStack.push(snapshot()); restore(next); } },
        canUndo: () => undoStack.length > 0,
        canRedo: () => redoStack.length > 0,
        clearHistory: () => { undoStack.length = 0; redoStack.length = 0; lastMutationAt = 0; set({ historyVersion: get().historyVersion + 1 }); }
      }
    };
  });
};

const SceneContext = createContext<ReturnType<typeof initialState> | null>(
  null
);

interface ProviderProps {
  children: React.ReactNode;
}

/**
 * SceneProvider: Provides Zustand store via React Context
 *
 * This pattern allows multiple independent store instances in the component tree
 * and prevents state sharing between different diagram instances. Each provider
 * creates its own store instance, enabling features like opening multiple diagrams
 * in different tabs/components.
 *
 * Reference: https://github.com/pmndrs/zustand/discussions/1180#discussioncomment-3439061
 */
export const SceneProvider = ({ children }: ProviderProps) => {
  const storeRef = useRef<ReturnType<typeof initialState>>();

  if (!storeRef.current) {
    storeRef.current = initialState();
  }

  return (
    <SceneContext.Provider value={storeRef.current}>
      {children}
    </SceneContext.Provider>
  );
};

export function useSceneStore<T>(
  selector: (state: SceneStore) => T,
  equalityFn?: (left: T, right: T) => boolean
) {
  const store = useContext(SceneContext);

  if (store === null) {
    throw new Error('Missing provider in the tree');
  }

  const value = useStore(store, selector, equalityFn);

  return value;
}
